import React from "react"

import { homeColor } from "../../home-theme.ts"

/**
 * The catalog card's thumbnail.
 *
 * Deliberately schematic: shapes for the widget's SHAPE, never numbers. A
 * thumbnail with figures on it is either a lie (invented) or a leak (real
 * data fetched to decorate a picker), and a trend line drawn on a widget that
 * reports a single total implies a capability that does not exist. Grey bars
 * promise only "this is a bar chart", which is all a picker has to say.
 */

const BAR = homeColor.accent
const BAR_SOFT = "#DCE7EC"
const LINE = "#DDE5EB"

const Frame = ({ children }: React.PropsWithChildren) => (
  <div
    aria-hidden
    data-testid="widget-preview"
    style={{
      height: 76,
      borderRadius: 8,
      border: `1px solid ${homeColor.border}`,
      background: homeColor.canvas,
      padding: 10,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 6,
      overflow: "hidden"
    }}
  >
    {children}
  </div>
)

const Row = ({ width, color = LINE, height = 6 }: { width: string; color?: string; height?: number }) => (
  <div style={{ width, height, borderRadius: 3, background: color, flexShrink: 0 }} />
)

const KpiPreview = () => (
  <Frame>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          background: homeColor.tealPale,
          border: `1px solid ${BAR_SOFT}`,
          flexShrink: 0
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <Row width="44px" height={5} />
        <Row width="64px" height={10} color={BAR} />
      </div>
    </div>
  </Frame>
)

const ChartPreview = () => (
  <Frame>
    <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 40 }}>
      {[40, 66, 30, 80, 52, 70].map((h, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: `${h}%`,
            borderRadius: 2,
            background: i % 2 === 0 ? BAR : BAR_SOFT
          }}
        />
      ))}
    </div>
  </Frame>
)

const DonutPreview = () => (
  <Frame>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          flexShrink: 0,
          background: `conic-gradient(${BAR} 0 62%, ${BAR_SOFT} 62% 100%)`,
          WebkitMask: "radial-gradient(circle, transparent 54%, #000 55%)",
          mask: "radial-gradient(circle, transparent 54%, #000 55%)"
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <Row width="52px" />
        <Row width="40px" />
        <Row width="46px" />
      </div>
    </div>
  </Frame>
)

const TablePreview = () => (
  <Frame>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <Row width="38%" height={5} color={BAR_SOFT} />
        <Row width="24%" height={5} color={BAR_SOFT} />
        <Row width="20%" height={5} color={BAR_SOFT} />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", gap: 6 }}>
          <Row width="46%" />
          <Row width="22%" />
          <Row width="16%" />
        </div>
      ))}
    </div>
  </Frame>
)

const SnapshotPreview = () => (
  <Frame>
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 3, height: 12, borderRadius: 2, background: BAR, flexShrink: 0 }} />
          <Row width="30%" height={5} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Row width="34px" />
            <Row width="34px" />
          </div>
        </div>
      ))}
    </div>
  </Frame>
)

const FeedPreview = () => (
  <Frame>
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: i === 0 ? homeColor.warningBg : homeColor.chipBg,
              border: `1px solid ${BAR_SOFT}`,
              flexShrink: 0
            }}
          />
          <Row width={i === 1 ? "58%" : "72%"} />
        </div>
      ))}
    </div>
  </Frame>
)

const MapPreview = () => (
  <Frame>
    <div
      style={{
        position: "relative",
        height: 48,
        borderRadius: 6,
        background: homeColor.surface,
        border: `1px solid ${homeColor.border}`,
        overflow: "hidden"
      }}
    >
      <div style={{ position: "absolute", inset: 0, opacity: 0.6 }}>
        <div style={{ position: "absolute", top: 16, left: 0, right: 0, height: 1, background: LINE }} />
        <div style={{ position: "absolute", top: 33, left: 0, right: 0, height: 1, background: LINE }} />
        <div style={{ position: "absolute", left: "33%", top: 0, bottom: 0, width: 1, background: LINE }} />
        <div style={{ position: "absolute", left: "66%", top: 0, bottom: 0, width: 1, background: LINE }} />
      </div>
      {[
        { left: "22%", top: "26%" },
        { left: "56%", top: "52%" },
        { left: "78%", top: "22%" }
      ].map((pos, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            ...pos,
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: BAR,
            border: `2px solid ${homeColor.surface}`
          }}
        />
      ))}
    </div>
  </Frame>
)

const DONUT_METRICS = new Set(["project_status_breakdown"])

/**
 * `type` is the registry's own shape field, so a widget this app has never
 * rendered still gets a thumbnail that matches what it will become.
 */
export const WidgetPreview = ({
  type,
  metric
}: {
  type: string
  metric?: string | null
}) => {
  if (metric && DONUT_METRICS.has(metric)) return <DonutPreview />
  switch (type) {
    case "kpi":
      return <KpiPreview />
    case "chart":
      return <ChartPreview />
    case "table":
    case "list":
      return <TablePreview />
    case "snapshot":
      return <SnapshotPreview />
    case "feed":
    case "alert":
      return <FeedPreview />
    case "map":
      return <MapPreview />
    default:
      return <TablePreview />
  }
}
